import {useCallback, useMemo, useState} from 'react';
import {
    View,
    Text,
    StyleSheet,
} from 'react-native';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {OfferCard} from './OfferCard';
import {useOrganizationOffers} from '@/data/offer-hooks';
import {AppDataOffer} from '@/data/app-data-interfaces';
import {HeaderText} from "@/components/common/HeaderText";
import {ThemeType} from "@/theming/baseTheme";
import {useOrganizationNavigation} from "@/hooks/useOrganizationNavigation";
import {useHardwareBack} from "@/hooks/useHardwareBack";
import {ResponsiveGrid} from "@/components/common/ResponsiveGrid";

interface OffersScreenProps {
    orgId: string;
}

export const OffersScreen = ({orgId}: OffersScreenProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const {data: offers} = useOrganizationOffers(orgId);
    const queryClient = useQueryClient();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const navigation = useOrganizationNavigation(orgId);

    const styles = createStyles(theme);

    // Purchased offers sink to the bottom of the list
    const sortedOffers = useMemo(() => {
        return [...(offers ?? [])].sort((a, b) => a.isPurchased ? 1 : b.isPurchased ? -1 : 0);
    }, [offers]);

    const handleBack = useCallback(() => {
        navigation.navigateBack();
    }, [navigation]);

    const handleOfferPress = useCallback((offer: AppDataOffer) => {
        navigation.navigateToOfferDetail(offer.id);
    }, [navigation]);

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await queryClient.invalidateQueries({queryKey: ['appData']});
        } finally {
            setIsRefreshing(false);
        }
    }, [queryClient]);

    const renderOffer = useCallback((offer: AppDataOffer) => (
        <OfferCard
            offer={offer}
            onPress={handleOfferPress}
            isPurchased={offer.isPurchased}
        />
    ), [handleOfferPress]);

    useHardwareBack(() => {
        handleBack();
        return true;
    });

    return (
        <View style={styles.container}>
            <HeaderText
                title={t('offers.title')}
                onBackPress={handleBack}
            />

            {sortedOffers.length === 0 ? (
                <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                        {t('offers.noOffers')}
                    </Text>
                </View>
            ) : (
                <ResponsiveGrid
                    data={sortedOffers}
                    renderItem={renderOffer}
                    keyExtractor={(offer) => offer.id}
                    minItemWidth={300}
                    maxItemWidth={420}
                    maxColumns={3}
                    isRefreshing={isRefreshing}
                    onRefresh={handleRefresh}
                    style={styles.grid}
                />
            )}
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    grid: {
        flex: 1,
        paddingHorizontal: theme.spacing.md,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    emptyText: {
        fontSize: 16,
        color: theme.colors.textSecondary,
        textAlign: 'center',
    },
});
